import { ISelectOption } from '~/shared/components/Form/Select/types';

import { IIntegrationApp } from '~/shared/services/api/interfaces/IIntegrationApp';

import { TSubscriberSubscriptionType } from '~/modules/subscribers/interfaces/ISubscriberSubscriptionContract';
import { ILoadResalesOptionsFilters } from './interfaces/ILoadResalesOptionsFilters';
import { ILoadResalesFilters } from './interfaces/ILoadResalesFilters';

import { ResaleUpdateStatusRequestDTO } from './dtos/ResaleUpdateStatusRequestDTO';
import {
  ResaleResponseDTO,
  ResaleOperatingAreaResponseDTO,
} from './dtos/ResaleResponseDTO';
import { ResaleRequestDTO } from './dtos/ResaleRequestDTO';
import { ResalePlansDTO } from './dtos/ResalePlansDTO';
import { ResaleItemsDTO } from './dtos/ResaleItemsDTO';
import { PaginationResponseDTO } from './dtos/PaginationResponseDTO';
import { AddActionRequestDTO } from './dtos/AddActionRequestDTO';

import { setupApi } from '.';

export async function loadResales(
  queries = {} as ILoadResalesFilters,
  ctx = undefined
): Promise<PaginationResponseDTO<ResaleResponseDTO[]>> {
  const response = await setupApi(ctx).get<
    PaginationResponseDTO<ResaleResponseDTO[]>
  >('/resales', { params: queries });

  return response.data;
}

export async function loadResaleById(
  id: string,
  ctx = undefined
): Promise<ResaleResponseDTO> {
  const response = await setupApi(ctx).get<ResaleResponseDTO>(`/resales/${id}`);
  return response.data;
}

export async function loadResalePlansById(
  id: string,
  ctx = undefined
): Promise<ResalePlansDTO> {
  const response = await setupApi(ctx).get<ResalePlansDTO>(
    `/resales/${id}/plans`
  );

  return response.data;
}

export async function loadResalesOptions(
  filters = {} as ILoadResalesOptionsFilters,
  ctx = undefined
): Promise<ISelectOption[]> {
  const response = await setupApi(ctx).get<ISelectOption[]>(
    '/resales/options',
    { params: filters }
  );

  return response.data || [];
}

export async function addActionToResales(
  ids: string[],
  data: AddActionRequestDTO,
  ctx = undefined
): Promise<any> {
  await Promise.all(
    ids.map(async (id) => {
      await setupApi(ctx).post(`/resales/${id}/actions`, {
        feedback: data.feedback,
        action: data.action._id,
      });
    })
  );
}

export async function updateResale(
  id: string,
  data: ResaleRequestDTO,
  ctx = undefined
): Promise<void> {
  await setupApi(ctx).put(`/resales/${id}`, data);
}

export async function createResale(
  data: ResaleRequestDTO,
  ctx = undefined
): Promise<void> {
  await setupApi(ctx).post('/resales', data);
}

export async function loadResaleIntegrationApp(
  id: string,
  ctx = undefined
): Promise<IIntegrationApp> {
  const response = await setupApi(ctx).get<IIntegrationApp>(
    `/resales/${id}/integration-app`
  );

  return response.data;
}

export async function saveResaleIntgrationApp(
  resaleId: string,
  permissionsIds: string[],
  ctx = undefined
): Promise<IIntegrationApp> {
  const response = await setupApi(ctx).post<IIntegrationApp>(
    `/resales/${resaleId}/integration-app`,
    { permissions: permissionsIds }
  );
  return response.data;
}

export async function loadAllResalesChildren(
  resaleId: string,
  ctx = undefined
): Promise<ResaleResponseDTO[]> {
  const response = await setupApi(ctx).get(`/resales/${resaleId}/children`);
  return response.data;
}

export async function updateResaleStatus(
  id: string,
  data: ResaleUpdateStatusRequestDTO,
  ctx = undefined
): Promise<ResaleResponseDTO> {
  const response = await setupApi(ctx).patch(`/resales/${id}/status`, data);
  return response.data;
}

export async function loadResaleOperatingArea(
  id: string,
  ctx = undefined
): Promise<ResaleOperatingAreaResponseDTO[]> {
  const response = await setupApi(ctx).get(`/resales/${id}/operating-area`);
  return response.data;
}

export async function loadResaleItemsById(
  id: string,
  type: TSubscriberSubscriptionType,
  ctx = undefined
): Promise<ResaleItemsDTO> {
  const response = await setupApi(ctx).get<ResaleItemsDTO>(
    `/resales/${id}/${type}/items`
  );
  return response.data;
}
